import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, MapPin, Phone, Mail, MessageCircle, Building2, Clock, Check, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { legalContent } from "../data/legalContent";

/**
 * Public legal pages for KZ "О персональных данных" compliance.
 * Three tabs: Contacts | Privacy Policy | Loyalty Terms
 * URL: /legal?tab=privacy|terms|contacts
 * Fully localized to RU / KZ / EN. Sticky action bar for Privacy/Terms tabs.
 */
export default function Legal() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { t, lang } = useLanguage();
  const tab = params.get("tab") || "contacts";

  const setTab = (id) => {
    setParams({ tab: id }, { replace: true });
    // Auto scroll to top on tab change
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
  };

  // Scroll to top on first mount too
  useEffect(() => { try { window.scrollTo({ top: 0 }); } catch { /* noop */ } }, [tab]);

  const handleConfirm = () => {
    // If opened in a new tab from Auth, close it. Otherwise return to /auth
    // with consent=1 so the checkbox is pre-selected.
    try {
      if (window.opener) { window.close(); return; }
    } catch { /* noop */ }
    navigate("/auth?consent=1");
  };

  const handleReject = () => {
    try {
      if (window.opener) { window.close(); return; }
    } catch { /* noop */ }
    navigate(-1);
  };

  const showActionBar = tab === "privacy" || tab === "terms";

  return (
    <div data-testid="legal-page" style={{ background: "var(--ang-bg)", minHeight: "100vh", color: "var(--ang-text)", paddingBottom: showActionBar ? "110px" : "60px" }}>
      {/* Top left: Back button */}
      <button onClick={() => navigate(-1)} data-testid="legal-back-btn"
        style={{ position: "fixed", top: "14px", left: "10px", background: "var(--ang-nav-bg)", backdropFilter: "blur(8px)", border: "1px solid var(--ang-border)", borderRadius: "999px", padding: "8px 14px 8px 10px", cursor: "pointer", color: "var(--ang-text)", display: "flex", alignItems: "center", gap: "4px", fontFamily: "Manrope", fontSize: "12px", zIndex: 10 }}>
        <ChevronLeft size={16} /> {t.legal.back}
      </button>

      {/* Top right: Language switcher */}
      <div style={{ position: "fixed", top: "14px", right: "10px", zIndex: 10, background: "rgba(10,10,10,0.7)", backdropFilter: "blur(8px)", padding: "4px", borderRadius: "999px", border: "1px solid var(--ang-border)" }}>
        <LanguageSwitcher dark={true} />
      </div>

      <div style={{ padding: "60px 20px 16px" }}>
        <h1 style={{ fontFamily: "Cormorant Garamond", fontSize: "30px", color: "var(--ang-text)", fontWeight: 400, marginBottom: "4px" }}>{t.legal.title}</h1>
        <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-gold-strong)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: "18px" }}>{t.legal.subtitle}</div>

        <div data-testid="legal-tabs" style={{ display: "flex", gap: "18px", borderBottom: "1px solid var(--ang-border)", marginBottom: "20px", overflowX: "auto" }}>
          {[
            { id: "contacts", label: t.legal.tabs.contacts },
            { id: "privacy", label: t.legal.tabs.privacy },
            { id: "terms", label: t.legal.tabs.terms },
          ].map(item => (
            <button key={item.id} data-testid={`legal-tab-${item.id}`} onClick={() => setTab(item.id)}
              style={{ background: "none", border: "none", color: tab === item.id ? "var(--ang-gold)" : "var(--ang-text-muted)", fontFamily: "Manrope", fontSize: "12px", fontWeight: tab === item.id ? 700 : 500, padding: "10px 0", cursor: "pointer", position: "relative", whiteSpace: "nowrap", letterSpacing: "0.04em" }}>
              {item.label}
              {tab === item.id && <span style={{ position: "absolute", left: 0, right: 0, bottom: "-1px", height: "2px", background: "var(--ang-gold)" }} />}
            </button>
          ))}
        </div>
      </div>

      <motion.div key={`${tab}-${lang}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} style={{ padding: "0 20px" }}>
        {tab === "contacts" && <ContactsSection t={t} />}
        {tab === "privacy" && <ArticleSection data={legalContent[lang]?.privacy || legalContent.ru.privacy} testId="privacy-section" />}
        {tab === "terms" && <ArticleSection data={legalContent[lang]?.terms || legalContent.ru.terms} testId="terms-section" />}
      </motion.div>

      {/* Sticky bottom action bar (Privacy & Terms only) */}
      {showActionBar && (
        <div data-testid="legal-action-bar" style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "linear-gradient(180deg, rgba(10,10,10,0.4), rgba(10,10,10,0.98) 40%)",
          backdropFilter: "blur(12px)",
          padding: "14px 16px 18px",
          borderTop: "1px solid var(--ang-gold-soft)",
          zIndex: 20,
        }}>
          <div style={{ maxWidth: "430px", margin: "0 auto", display: "flex", gap: "10px" }}>
            <button data-testid="legal-reject-btn" onClick={handleReject}
              style={{ flex: "0 0 auto", padding: "13px 16px", background: "transparent", border: "1px solid var(--ang-border-strong)", borderRadius: "2px", color: "var(--ang-text-soft)", fontFamily: "Manrope", fontSize: "11px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
              <X size={14} /> {t.legal.actions.reject}
            </button>
            <button data-testid="legal-confirm-btn" onClick={handleConfirm}
              style={{ flex: 1, padding: "13px 16px", background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", border: "none", borderRadius: "2px", color: "var(--ang-bg)", fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
              <Check size={15} /> {t.legal.actions.confirm}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ────────── CONTACTS ────────── */
function ContactsSection({ t }) {
  const c = t.legal.contacts;
  const rows = [
    { icon: Building2, label: c.boutique, value: "Angiolini" },
    { icon: MapPin, label: c.address, value: c.addressValue },
    { icon: Phone, label: c.phone, value: "+7 701 801 77 55", href: "tel:+77018017755" },
    { icon: MessageCircle, label: c.whatsapp, value: "+7 701 801 77 55", href: "https://wa.me/77018017755" },
    { icon: Mail, label: c.email, value: "angiolinikz@gmail.com", href: "mailto:angiolinikz@gmail.com" },
    { icon: Clock, label: c.hours, value: c.hoursValue },
  ];
  return (
    <div data-testid="contacts-section" style={{ display: "grid", gap: "10px" }}>
      {rows.map(r => (
        <a key={r.label} href={r.href || undefined} target={r.href?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
          style={{ background: "var(--ang-surface-2)", border: "1px solid var(--ang-border)", borderRadius: "8px", padding: "14px 16px", display: "flex", alignItems: "center", gap: "14px", textDecoration: "none", color: "var(--ang-text)", cursor: r.href ? "pointer" : "default" }}>
          <div style={{ flexShrink: 0, width: "38px", height: "38px", borderRadius: "50%", background: "var(--ang-gold-soft)", border: "1px solid var(--ang-gold-line)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <r.icon size={16} style={{ color: "var(--ang-gold)" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-text-faint)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "2px" }}>{r.label}</div>
            <div style={{ fontFamily: "Cormorant Garamond", fontSize: "16px", color: "var(--ang-text)", whiteSpace: "pre-line", lineHeight: 1.35 }}>{r.value}</div>
          </div>
        </a>
      ))}

      <div style={{ marginTop: "20px", padding: "16px", background: "var(--ang-surface-2)", border: "1px solid var(--ang-border)", borderRadius: "8px" }}>
        <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-gold-strong)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: "10px", fontWeight: 600 }}>{c.legalHeader}</div>
        <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-soft)", lineHeight: 1.7, whiteSpace: "pre-line" }}>
          {c.legalDetails}
        </div>
      </div>
    </div>
  );
}

/* ────────── ARTICLE (Privacy / Terms shared renderer) ────────── */
function ArticleSection({ data, testId }) {
  return (
    <article data-testid={testId} style={proseStyle}>
      <p style={metaStyle}>{data.effective}</p>
      {data.sections.map((s, idx) => (
        <React.Fragment key={idx}>
          <h2 style={h2}>{s.h2}</h2>
          {s.blocks.map((b, i) => {
            if (b.p) return <p key={i} style={{ whiteSpace: "pre-line" }}>{renderInline(b.p)}</p>;
            if (b.ul) return <ul key={i} style={ul}>{b.ul.map((li, j) => <li key={j}>{renderInline(li)}</li>)}</ul>;
            if (b.ulNested) return <ul key={i} style={ulNested}>{b.ulNested.map((li, j) => <li key={j}>{renderInline(li)}</li>)}</ul>;
            return null;
          })}
        </React.Fragment>
      ))}
    </article>
  );
}

/**
 * Render minimal markdown-like inline formatting: **bold** and [text](url).
 * The legal content author writes with these markers to keep the source readable.
 */
function renderInline(text) {
  if (!text) return null;
  // Tokenize: split by markdown [text](url) or **bold** while preserving order
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    parts.push(m[0]);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  return parts.map((p, i) => {
    if (typeof p !== "string") return p;
    if (p.startsWith("**") && p.endsWith("**")) {
      return <b key={i}>{p.slice(2, -2)}</b>;
    }
    const link = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      return <a key={i} href={link[2]} style={linkStyle} target={link[2].startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">{link[1]}</a>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

/* ────────── styles ────────── */
const proseStyle = { fontFamily: "Manrope", fontSize: "13.5px", color: "var(--ang-text-soft)", lineHeight: 1.7, paddingBottom: "32px" };
const metaStyle = { fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-gold-strong)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "20px", fontWeight: 600 };
const h2 = { fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", fontWeight: 500, margin: "26px 0 10px", borderTop: "1px solid var(--ang-border)", paddingTop: "20px" };
const ul = { paddingLeft: "20px", margin: "0 0 14px" };
const ulNested = { paddingLeft: "20px", margin: "4px 0", listStyleType: "circle" };
const linkStyle = { color: "var(--ang-gold)", textDecoration: "underline" };
